import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Wallet, Search, AlertTriangle, CheckCircle2, RefreshCw, Settings } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;

export default function AdminWallets() {
  const [tab, setTab] = useState("customers");
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [divergent, setDivergent] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");

  const loadCustomers = async () => {
    const { data } = await api.get(`/admin/wallets/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
    setCustomers(data);
  };
  const loadCompanies = async () => {
    const { data } = await api.get(`/admin/wallets/companies${search ? `?search=${encodeURIComponent(search)}` : ""}`);
    setCompanies(data);
  };
  const loadDivergent = async () => {
    const { data } = await api.get(`/admin/wallets/divergences`);
    setDivergent(data);
  };

  useEffect(() => {
    if (tab === "customers") loadCustomers();
    else if (tab === "companies") loadCompanies();
    else if (tab === "divergences") loadDivergent();
    /* eslint-disable-next-line */
  }, [tab]);

  const openDetail = async (u) => {
    const { data } = await api.get(`/admin/wallets/customers/${u.id}`);
    setSelected({ ...data, summary: u });
  };

  const audit = async (autoFix) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/wallets/customers/${selected.user.id}/audit`, { auto_fix: autoFix });
      if (data.applied) toast.success(`Ajuste de ${fmt(data.applied.amount)} aplicado.`);
      else if (Math.abs(data.diff) < 0.005) toast.success("Sem divergência.");
      else toast.info(`Divergência: ${fmt(data.diff)} (não corrigida)`);
      openDetail(selected.summary);
      if (tab === "divergences") loadDivergent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const adjust = async () => {
    const v = parseFloat(adjAmount);
    if (!v || !adjReason.trim()) { toast.error("Informe valor (positivo ou negativo) e motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/wallets/customers/${selected.user.id}/adjust`, { amount: v, reason: adjReason });
      toast.success("Ajuste lançado");
      setAdjAmount(""); setAdjReason("");
      openDetail(selected.summary);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <Link to="/admin" className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mb-1"><ArrowLeft size={14}/> Voltar</Link>
          <h1 className="font-display text-3xl font-bold text-stone-900 flex items-center gap-2"><Wallet size={26}/> Carteiras</h1>
        </div>
        <Link to="/admin/notifications" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-stone-900 text-white hover:bg-stone-800"><Settings size={14}/> Notificações</Link>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-2">
        <TabsList className="bg-stone-100 rounded-xl">
          <TabsTrigger value="customers" data-testid="tab-customers">Clientes</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">Empresas</TabsTrigger>
          <TabsTrigger value="divergences" data-testid="tab-divergences"><AlertTriangle size={13} className="mr-1"/>Divergências</TabsTrigger>
        </TabsList>

        {tab !== "divergences" && (
          <Card className="mt-4 p-3 rounded-2xl border-stone-200 flex gap-2 items-center">
            <Search size={14} className="text-stone-400 ml-2"/>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome / email / código..." className="rounded-xl flex-1 border-0 focus-visible:ring-0" data-testid="wallet-search"/>
            <Button onClick={() => tab === "customers" ? loadCustomers() : loadCompanies()} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" size="sm">Buscar</Button>
          </Card>
        )}

        <TabsContent value="customers" className="mt-4">
          <Card className="rounded-2xl border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Cliente</th><th>Wallet Code</th><th className="text-right">Saldo</th><th className="text-right">Teórico</th><th className="text-right">Δ</th><th></th></tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3">{c.name}<div className="text-[11px] text-stone-500">{c.email}</div></td>
                      <td className="font-mono text-[12px]">{c.wallet_code}</td>
                      <td className="text-right font-bold">{fmt(c.balance)}</td>
                      <td className="text-right text-stone-600">{fmt(c.theoretical)}</td>
                      <td className={`text-right font-bold ${Math.abs(c.diff) < 0.005 ? "text-emerald-700" : "text-red-700"}`}>{fmt(c.diff)}</td>
                      <td className="text-right pr-4"><Button size="sm" variant="outline" onClick={() => openDetail(c)} className="rounded-lg text-xs" data-testid={`wallet-open-${c.id}`}>Detalhar</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="companies" className="mt-4">
          <Card className="rounded-2xl border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Empresa</th><th>Cidade</th><th>Plano</th><th className="text-right">Saldo</th><th className="text-right">Teórico</th><th className="text-right">Δ</th></tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} className="border-t border-stone-100">
                      <td className="px-4 py-3">{c.name}{c.active === false && <span className="ml-2 text-[10px] uppercase text-red-700 bg-red-100 px-2 py-0.5 rounded">Inativa</span>}</td>
                      <td>{c.city_slug}</td>
                      <td><span className="text-[10px] font-bold">{c.plan}</span></td>
                      <td className="text-right font-bold">{fmt(c.company_wallet_balance)}</td>
                      <td className="text-right text-stone-600">{fmt(c.theoretical)}</td>
                      <td className={`text-right font-bold ${Math.abs(c.diff) < 0.005 ? "text-emerald-700" : "text-red-700"}`}>{fmt(c.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="divergences" className="mt-4">
          <div className="flex justify-between mb-3">
            <p className="text-sm text-stone-600">Clientes com divergência de saldo (saldo guardado ≠ soma do histórico).</p>
            <Button onClick={loadDivergent} variant="outline" size="sm" className="rounded-xl" data-testid="reload-divergences"><RefreshCw size={13} className="mr-1"/> Atualizar</Button>
          </div>
          <Card className="rounded-2xl border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-amber-900 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Cliente</th><th>Wallet Code</th><th className="text-right">Saldo</th><th className="text-right">Teórico</th><th className="text-right">Δ</th><th></th></tr>
                </thead>
                <tbody>
                  {divergent.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-emerald-700"><CheckCircle2 size={24} className="mx-auto mb-1"/>Tudo conciliado!</td></tr>}
                  {divergent.map((c) => (
                    <tr key={c.id} className="border-t border-stone-100">
                      <td className="px-4 py-3">{c.name}<div className="text-[11px] text-stone-500">{c.email}</div></td>
                      <td className="font-mono text-[12px]">{c.wallet_code}</td>
                      <td className="text-right">{fmt(c.balance)}</td>
                      <td className="text-right">{fmt(c.theoretical)}</td>
                      <td className="text-right text-red-700 font-bold">{fmt(c.diff)}</td>
                      <td className="text-right pr-4"><Button size="sm" onClick={() => openDetail(c)} className="rounded-lg text-xs bg-blue-900 text-white" data-testid={`div-fix-${c.id}`}>Resolver</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {selected && (
        <Dialog open onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <DialogHeader><DialogTitle className="font-display">Carteira de {selected.user.name}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <Card className="p-4 rounded-xl bg-blue-50 border-blue-200"><div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Saldo atual</div><div className="font-display text-2xl text-blue-950">{fmt(selected.wallet.balance)}</div></Card>
              <Card className="p-4 rounded-xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Wallet Code</div><div className="font-mono text-lg">{selected.user.wallet_code}</div></Card>
              <Card className="p-4 rounded-xl bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold">Email</div><div className="text-xs">{selected.user.email}</div></Card>
            </div>

            <Card className="p-4 rounded-xl border-stone-200 mt-4">
              <div className="flex flex-wrap items-end gap-2">
                <Button onClick={() => audit(false)} disabled={busy} variant="outline" className="rounded-xl" data-testid="audit-check"><RefreshCw size={13} className="mr-1"/>Auditar (apenas verificar)</Button>
                <Button onClick={() => audit(true)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="audit-fix"><CheckCircle2 size={13} className="mr-1"/>Auditar e Corrigir Automaticamente</Button>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-200">
                <Label className="text-sm font-semibold">Ajuste manual</Label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  <Input type="number" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="+ ou - valor" className="rounded-xl" data-testid="adj-amount"/>
                  <Input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="Motivo do ajuste" className="rounded-xl sm:col-span-1" data-testid="adj-reason"/>
                  <Button onClick={adjust} disabled={busy} className="rounded-xl bg-blue-900 text-white" data-testid="adj-submit">Lançar ajuste</Button>
                </div>
              </div>
            </Card>

            <h4 className="font-display font-semibold mt-5 mb-2">Histórico de cashback ({selected.transactions.length})</h4>
            <div className="divide-y divide-stone-100 text-sm max-h-60 overflow-auto rounded-xl border border-stone-200">
              {selected.transactions.length === 0 && <p className="px-3 py-3 text-stone-500">Sem transações.</p>}
              {selected.transactions.map((t) => (
                <div key={t.id} className="px-3 py-2 flex justify-between">
                  <div>
                    <div>{t.company_name}</div>
                    <div className="text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString("pt-BR")} · {t.type}</div>
                  </div>
                  <div className={`font-bold ${t.type === "CREDIT" ? "text-emerald-700" : "text-amber-700"}`}>{t.type === "CREDIT" ? "+" : "−"}{fmt(t.cashback_value)}</div>
                </div>
              ))}
            </div>

            {selected.adjustments.length > 0 && (
              <>
                <h4 className="font-display font-semibold mt-4 mb-2">Ajustes manuais ({selected.adjustments.length})</h4>
                <div className="divide-y divide-stone-100 text-sm max-h-40 overflow-auto rounded-xl border border-stone-200">
                  {selected.adjustments.map((a) => (
                    <div key={a.id} className="px-3 py-2 flex justify-between">
                      <div>
                        <div>{a.reason}</div>
                        <div className="text-[11px] text-stone-500">{new Date(a.created_at).toLocaleString("pt-BR")}</div>
                      </div>
                      <div className={`font-bold ${a.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>{a.amount >= 0 ? "+" : ""}{fmt(a.amount)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
